/**
 * exitCalc.ts
 *
 * Exact TypeScript port of Exit Strategy sheet formulas:
 *
 *   D7  = SUMIFS(PL!$57:$57, PL!$3:$3, D3)   → referenceEBITDA
 *   D8  = D4                                   → exitCapRate (decimal)
 *   D9  = D7 / D8                              → estimatedSalePrice
 *   D10 = -D5 * D9                             → sellingCosts  (positive number, represents a cost)
 *   D11 = SUM(D9:D10)                          → netSaleProceeds = salePrice + sellingCosts(signed)
 *   D12 = -Investment Budget!D29               → lessCapEx (negative)
 *   D13 = SUM(D11:D12)                         → developmentProfit
 */

import type { Deal } from "../types/deal";
import type { ExitSettings } from "../types/exitStrategy";
import { calculatePLData } from "./pl/plCalculations";

// ─── helpers ────────────────────────────────────────────────────────────────

/**
 * Resolve the active exit year from ExitSettings (UI state).
 *
 * Priority:
 *   SALE      → sale.exitYear
 *   REFINANCE → refinance.refinanceYear
 *   fallback  → 10
 *
 * Unlike the old getExitYear(deal) this reads from the live exitSettings
 * object (which the user controls in the UI), not the stale deal.kpis.exit.
 */
export function resolveExitYear(exitSettings: ExitSettings): number {
  if (exitSettings.strategy === "SALE") {
    return exitSettings.sale?.exitYear ?? 10;
  }
  if (exitSettings.strategy === "REFINANCE") {
    return exitSettings.refinance?.refinanceYear ?? 10;
  }
  // HOLD_FOREVER — no terminal event; callers should not need this value,
  // but return the full horizon so nothing breaks.
  return 10;
}

/**
 * Build { y1: ebitda, y2: ebitda, … } from raw (un-horizoned) PL rows.
 * Equivalent to SUMIFS(PL!$57:$57, PL!$3:$3, exitYear)
 */
function buildEbitdaByYear(deal: Deal): Record<string, number> {
  const rawRows = calculatePLData(deal);
  const ebitdaRow = rawRows.find((r) => r.id === "ebitda");
  if (!ebitdaRow) return {};

  const result: Record<string, number> = {};
  ebitdaRow.years.forEach((y) => {
    result[`y${y.year}`] = y.total;
  });
  return result;
}

// ─── Sale Summary ────────────────────────────────────────────────────────────

export interface SaleSummary {
  /** D7 — EBITDA for the selected exit year */
  referenceEBITDA: number;
  /** D8 — cap rate as a percentage number (e.g. 5.5) */
  exitCapRate: number;
  /** D9 = D7 / (D8/100) */
  estimatedSalePrice: number;
  /**
   * D10 = -D5 * D9
   * Stored as the absolute (positive) cost amount for display purposes.
   * The signed value fed into netSaleProceeds is negative when salePrice > 0
   * and positive when salePrice < 0 — matching Excel exactly.
   */
  sellingCosts: number;
  /** D11 = SUM(D9:D10)  i.e. salePrice + (signedSellingCosts) */
  netSaleProceeds: number;
  /** absolute value of D12 = -IB!D29, for display as "Less: Total CapEx" */
  totalCapEx: number;
  /** D13 = SUM(D11:D12) = netSaleProceeds - totalCapEx */
  developmentProfit: number;
}

export function calculateSaleSummary(
  exitSettings: ExitSettings,
  projectCost: number, // Investment Budget D29  (positive)
  deal: Deal,
  /** Optional pre-built map; pass undefined to auto-compute from deal */
  ebitdaByYear?: Record<string, number>,
): SaleSummary {
  const map = ebitdaByYear ?? buildEbitdaByYear(deal);

  // D3 — exit year: read from live exitSettings, never from stale deal.kpis.exit
  const exitYear = resolveExitYear(exitSettings);

  // D7 = SUMIFS(PL!$57:$57, PL!$3:$3, D3)
  const referenceEBITDA = map[`y${exitYear}`] ?? 0;

  console.debug(
    "[exitCalc] strategy:",
    exitSettings.strategy,
    "exitYear:",
    exitYear,
    "ebitdaMap keys:",
    Object.keys(map),
    "referenceEBITDA:",
    referenceEBITDA,
  );

  // D8 = D4 (exitCapRate stored as percent, e.g. 5.5)
  const exitCapRatePct = exitSettings.sale.exitCapRate;

  // D9 = D7 / D8   (D8 is the raw fraction in Excel: 0.055)
  const estimatedSalePrice =
    exitCapRatePct > 0 ? referenceEBITDA / (exitCapRatePct / 100) : 0;

  // D10 = -D5 * D9   (D5 = sellingCostsPct as fraction, e.g. 0.03)
  const sellingCostsPct = exitSettings.sale.sellingCostsPct / 100;
  const signedSellingCosts = -sellingCostsPct * estimatedSalePrice; // matches Excel D10

  // D11 = SUM(D9:D10) = estimatedSalePrice + signedSellingCosts
  const netSaleProceeds = estimatedSalePrice + signedSellingCosts;

  // D12 = -Investment Budget!D29  (always negative)
  const lessCapEx = -projectCost;

  // D13 = SUM(D11:D12)
  const developmentProfit = netSaleProceeds + lessCapEx;

  return {
    referenceEBITDA,
    exitCapRate: exitCapRatePct,
    estimatedSalePrice,
    // signedSellingCosts already has the correct sign from Excel (D10 = -D5*D9).
    // Pass as-is — the UI will display it correctly without prepending its own minus.
    sellingCosts: signedSellingCosts,
    netSaleProceeds,
    totalCapEx: projectCost, // absolute, UI adds minus sign
    developmentProfit,
  };
}

// ─── Refinance Summary ───────────────────────────────────────────────────────

export interface RefinanceSummary {
  referenceEBITDA: number;
  exitCapRate: number;
  estimatedValue: number;
  loanAmount: number;
  existingDebt: number;
  netCashOut: number;
}

export function calculateRefinanceSummary(
  exitSettings: ExitSettings,
  projectCost: number,
  deal: Deal,
  ebitdaByYear?: Record<string, number>,
): RefinanceSummary {
  const map = ebitdaByYear ?? buildEbitdaByYear(deal);
  const exitYear = resolveExitYear(exitSettings);
  const referenceEBITDA = map[`y${exitYear}`] ?? 0;

  // Stub — no Excel formulas confirmed yet for Refinance sheet.
  // Using sale cap rate as proxy for property valuation.
  const exitCapRatePct = exitSettings.sale.exitCapRate;
  const estimatedValue =
    exitCapRatePct > 0 ? referenceEBITDA / (exitCapRatePct / 100) : 0;
  const ltvPct = exitSettings.refinance.ltvAtRefinance / 100;
  const loanAmount = estimatedValue * ltvPct;
  const existingDebt = deal?.kpis?.financing
    ? (deal.kpis.financing.ltcPct / 100) * projectCost
    : 0;
  const netCashOut = loanAmount - existingDebt;

  return {
    referenceEBITDA,
    exitCapRate: exitCapRatePct,
    estimatedValue,
    loanAmount,
    existingDebt,
    netCashOut,
  };
}
